/**
 * Render anchor — the float32 half of the FP64 origin rebase (2026-08-23).
 *
 * AtlasOriginRebaseModule holds `scene.position` at minus-the-current-origin so
 * an ordinary child's huge translation cancels in f64 on the CPU before the
 * f32 GPU upload. INSTANCE matrices never get that treatment: InstancedMesh /
 * BatchedMesh keep them in a float32 attribute and the vertex shader adds the
 * ~7e5 m translation to each vertex BEFORE anything cancels — at 720 km the
 * float32 ulp is 6 cm, so every vertex snaps to a 6 cm grid. That was the
 * "versonas/props look shattered up close, fine at a distance, structures
 * fine" report: big structures are plain meshes (CPU path), the small things
 * are instanced.
 *
 * Every instanced pool therefore parents its meshes under an ANCHOR GROUP
 * positioned at the current origin and stores matrices RELATIVE to it
 * (offsets ≤ the 10 km rebase threshold → ≤ 1 mm ulp). The anchor only moves
 * when the rebase module moves the origin; subscribers rewrite their stored
 * matrices then (rare: once per 10 km travelled).
 */
import * as THREE from 'three/webgpu';

let anchorX = 0;
let anchorZ = 0;
const listeners = new Set<(x: number, z: number) => void>();

/** The current render anchor (absolute world metres, integer). */
export function renderAnchor(): { x: number; z: number } { return { x: anchorX, z: anchorZ }; }
export function renderAnchorX(): number { return anchorX; }
export function renderAnchorZ(): number { return anchorZ; }

/** Move the anchor (the rebase module is the only intended writer). No-op when unchanged. */
export function setRenderAnchor(x: number, z: number): void {
  if (x === anchorX && z === anchorZ) return;
  anchorX = x; anchorZ = z;
  for (const cb of [...listeners]) cb(x, z);
}

/** Subscribe to anchor moves; returns the unsubscribe. */
export function onRenderAnchor(cb: (x: number, z: number) => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** `out` = `m` with the anchor subtracted from its translation (anchor-relative copy). */
export function toAnchorLocal(m: THREE.Matrix4, out: THREE.Matrix4): THREE.Matrix4 {
  out.copy(m);
  out.elements[12] -= anchorX;
  out.elements[14] -= anchorZ;
  return out;
}

/**
 * A group parked at the anchor (absolute coords — the scene rebase then
 * makes its matrixWorld small). Children must carry ANCHOR-RELATIVE transforms.
 */
export function makeAnchorGroup(name: string): { group: THREE.Group; release: () => void } {
  const group = new THREE.Group();
  group.name = name;
  group.position.set(anchorX, 0, anchorZ);
  const off = onRenderAnchor((x, z) => { group.position.set(x, 0, z); group.updateMatrixWorld(true); });
  return { group, release: off };
}

/** Test hook: reset the anchor + listeners (harnesses only). */
export function __resetRenderAnchor(): void { anchorX = 0; anchorZ = 0; listeners.clear(); }
